/**
 * ============================================================================
 * [P4-01] MetricManageModal Component
 *
 * 메트릭 관리 모달 - 메트릭 추가/수정/삭제/일괄삭제 기능
 *
 * 기능:
 * - 메트릭 목록 표시 (날짜순 정렬)
 * - 개별 메트릭 편집/삭제
 * - 배치 모드: 다중 선택 및 일괄 삭제
 * - 새 메트릭 추가
 *
 * Safety:
 * - 삭제 전 확인 다이얼로그
 * - 낙관적 업데이트 + 롤백 (useTrackingPosts에서 처리)
 * ============================================================================
 */

import { useState, useMemo, useCallback } from 'react'
import type { TrackingPost, TrackingMetric } from '@/types'
import styles from './Tracking.module.css'

// ============================================================================
// Props Interface
// ============================================================================
interface MetricManageModalProps {
  post: TrackingPost
  onClose: () => void
  onAddMetric: (metric: Omit<TrackingMetric, 'id'>) => Promise<void>
  onUpdateMetric: (metricId: string, updates: Partial<TrackingMetric>) => Promise<void>
  onDeleteMetric: (metricId: string) => Promise<void>
  onBatchDeleteMetrics: (metricIds: string[]) => Promise<void>
}

// ============================================================================
// 초기 메트릭 상태
// ============================================================================
const getInitialMetricState = (): Omit<TrackingMetric, 'id'> => ({
  date: new Date().toISOString().split('T')[0],
  views: 0,
  likes: 0,
  comments: 0,
  shares: 0,
  follows: 0,
  saves: 0,
  notes: '',
})

// ============================================================================
// Component
// ============================================================================
export default function MetricManageModal({
  post,
  onClose,
  onAddMetric,
  onUpdateMetric,
  onDeleteMetric,
  onBatchDeleteMetrics,
}: MetricManageModalProps) {
  // --------------------------------------------------------------------------
  // State
  // --------------------------------------------------------------------------
  // 배치 모드 상태
  const [isBatchMode, setIsBatchMode] = useState(false)
  const [selectedMetricIds, setSelectedMetricIds] = useState<string[]>([])

  // 편집 상태
  const [editingMetricId, setEditingMetricId] = useState<string | null>(null)
  const [editingMetric, setEditingMetric] = useState<Partial<TrackingMetric>>({})

  // 추가 모드 상태
  const [isAddMode, setIsAddMode] = useState(false)
  const [newMetric, setNewMetric] = useState<Omit<TrackingMetric, 'id'>>(getInitialMetricState())

  // 로딩 상태
  const [isLoading, setIsLoading] = useState(false)

  // --------------------------------------------------------------------------
  // 정렬된 메트릭 목록 (최신순)
  // --------------------------------------------------------------------------
  const sortedMetrics = useMemo(() => {
    return [...post.metrics].sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime()
    })
  }, [post.metrics])

  // --------------------------------------------------------------------------
  // Handlers: 배치 모드
  // --------------------------------------------------------------------------
  const toggleBatchMode = useCallback(() => {
    setIsBatchMode((prev) => !prev)
    setSelectedMetricIds([])
    setEditingMetricId(null)
  }, [])

  const toggleMetricSelection = useCallback((metricId: string) => {
    setSelectedMetricIds((prev) =>
      prev.includes(metricId)
        ? prev.filter((id) => id !== metricId)
        : [...prev, metricId]
    )
  }, [])

  const selectAllMetrics = useCallback(() => {
    setSelectedMetricIds(sortedMetrics.map((m) => m.id))
  }, [sortedMetrics])

  const clearSelection = useCallback(() => {
    setSelectedMetricIds([])
  }, [])

  // --------------------------------------------------------------------------
  // Handlers: 배치 삭제
  // --------------------------------------------------------------------------
  const handleBatchDelete = useCallback(async () => {
    if (selectedMetricIds.length === 0) return

    const confirmMsg = `선택한 ${selectedMetricIds.length}개의 메트릭을 삭제하시겠습니까?`
    if (!window.confirm(confirmMsg)) return

    setIsLoading(true)
    try {
      await onBatchDeleteMetrics(selectedMetricIds)
      setSelectedMetricIds([])
      setIsBatchMode(false)
    } catch (error) {
      console.error('[MetricManageModal] 배치 삭제 실패:', error)
      alert('메트릭 삭제에 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }, [selectedMetricIds, onBatchDeleteMetrics])

  // --------------------------------------------------------------------------
  // Handlers: 개별 편집
  // --------------------------------------------------------------------------
  const startEdit = useCallback((metric: TrackingMetric) => {
    setEditingMetricId(metric.id)
    setEditingMetric({ ...metric })
    setIsBatchMode(false)
  }, [])

  const cancelEdit = useCallback(() => {
    setEditingMetricId(null)
    setEditingMetric({})
  }, [])

  const saveEdit = useCallback(async () => {
    if (!editingMetricId) return

    setIsLoading(true)
    try {
      await onUpdateMetric(editingMetricId, editingMetric)
      setEditingMetricId(null)
      setEditingMetric({})
    } catch (error) {
      console.error('[MetricManageModal] 메트릭 수정 실패:', error)
      alert('메트릭 수정에 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }, [editingMetricId, editingMetric, onUpdateMetric])

  // --------------------------------------------------------------------------
  // Handlers: 개별 삭제
  // --------------------------------------------------------------------------
  const handleDeleteMetric = useCallback(async (metricId: string) => {
    if (!window.confirm('이 메트릭을 삭제하시겠습니까?')) return

    setIsLoading(true)
    try {
      await onDeleteMetric(metricId)
    } catch (error) {
      console.error('[MetricManageModal] 메트릭 삭제 실패:', error)
      alert('메트릭 삭제에 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }, [onDeleteMetric])

  // --------------------------------------------------------------------------
  // Handlers: 메트릭 추가
  // --------------------------------------------------------------------------
  const startAdd = useCallback(() => {
    setIsAddMode(true)
    setNewMetric(getInitialMetricState())
    setEditingMetricId(null)
    setIsBatchMode(false)
  }, [])

  const cancelAdd = useCallback(() => {
    setIsAddMode(false)
    setNewMetric(getInitialMetricState())
  }, [])

  const saveNewMetric = useCallback(async () => {
    if (!newMetric.date) {
      alert('날짜를 입력해주세요.')
      return
    }

    setIsLoading(true)
    try {
      await onAddMetric(newMetric)
      setIsAddMode(false)
      setNewMetric(getInitialMetricState())
    } catch (error) {
      console.error('[MetricManageModal] 메트릭 추가 실패:', error)
      alert('메트릭 추가에 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }, [newMetric, onAddMetric])

  // --------------------------------------------------------------------------
  // Render: 메트릭 편집 폼
  // --------------------------------------------------------------------------
  const renderMetricForm = (
    metric: Partial<TrackingMetric> | Omit<TrackingMetric, 'id'>,
    onChange: (updates: Partial<TrackingMetric>) => void,
    onSave: () => void,
    onCancel: () => void,
    isNew: boolean = false
  ) => (
    <div className={styles.metricEditForm}>
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label>날짜</label>
          <input
            type="date"
            className={styles.formInput}
            value={metric.date || ''}
            onChange={(e) => onChange({ date: e.target.value })}
          />
        </div>
      </div>

      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label>조회수</label>
          <input
            type="number"
            className={styles.formInput}
            value={metric.views ?? 0}
            onChange={(e) => onChange({ views: parseInt(e.target.value) || 0 })}
            min="0"
          />
        </div>
        <div className={styles.formGroup}>
          <label>좋아요</label>
          <input
            type="number"
            className={styles.formInput}
            value={metric.likes ?? 0}
            onChange={(e) => onChange({ likes: parseInt(e.target.value) || 0 })}
            min="0"
          />
        </div>
      </div>

      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label>댓글</label>
          <input
            type="number"
            className={styles.formInput}
            value={metric.comments ?? 0}
            onChange={(e) => onChange({ comments: parseInt(e.target.value) || 0 })}
            min="0"
          />
        </div>
        <div className={styles.formGroup}>
          <label>공유</label>
          <input
            type="number"
            className={styles.formInput}
            value={metric.shares ?? 0}
            onChange={(e) => onChange({ shares: parseInt(e.target.value) || 0 })}
            min="0"
          />
        </div>
      </div>

      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label>팔로우</label>
          <input
            type="number"
            className={styles.formInput}
            value={metric.follows ?? 0}
            onChange={(e) => onChange({ follows: parseInt(e.target.value) || 0 })}
            min="0"
          />
        </div>
        <div className={styles.formGroup}>
          <label>저장</label>
          <input
            type="number"
            className={styles.formInput}
            value={metric.saves ?? 0}
            onChange={(e) => onChange({ saves: parseInt(e.target.value) || 0 })}
            min="0"
          />
        </div>
      </div>

      <div className={styles.formGroup}>
        <label>메모</label>
        <textarea
          className={styles.formTextarea}
          value={metric.notes || ''}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="메트릭에 대한 메모를 입력하세요..."
          rows={2}
        />
      </div>

      <div className={styles.metricFormActions}>
        <button
          type="button"
          className={styles.cancelBtn}
          onClick={onCancel}
          disabled={isLoading}
        >
          취소
        </button>
        <button
          type="button"
          className={styles.submitBtn}
          onClick={onSave}
          disabled={isLoading}
        >
          {isLoading ? '저장 중...' : isNew ? '추가' : '저장'}
        </button>
      </div>
    </div>
  )

  // --------------------------------------------------------------------------
  // Render: 메트릭 아이템
  // --------------------------------------------------------------------------
  const renderMetricItem = (metric: TrackingMetric) => {
    const isEditing = editingMetricId === metric.id
    const isSelected = selectedMetricIds.includes(metric.id)

    if (isEditing) {
      return (
        <div key={metric.id} className={styles.metricListItem}>
          {renderMetricForm(
            editingMetric,
            (updates) => setEditingMetric((prev) => ({ ...prev, ...updates })),
            saveEdit,
            cancelEdit,
            false
          )}
        </div>
      )
    }

    return (
      <div
        key={metric.id}
        className={`${styles.metricListItem} ${isSelected ? styles.selected : ''}`}
      >
        {/* 배치 모드: 체크박스 */}
        {isBatchMode && (
          <input
            type="checkbox"
            className={styles.metricCheckbox}
            checked={isSelected}
            onChange={() => toggleMetricSelection(metric.id)}
          />
        )}

        {/* 메트릭 정보 */}
        <div className={styles.metricInfo}>
          <div className={styles.metricDate}>{metric.date}</div>
          <div className={styles.metricStats}>
            <span>👁 {metric.views.toLocaleString()}</span>
            <span>❤️ {metric.likes.toLocaleString()}</span>
            <span>💬 {metric.comments.toLocaleString()}</span>
            <span>🔄 {metric.shares.toLocaleString()}</span>
            {metric.follows !== undefined && metric.follows > 0 && (
              <span>👥 {metric.follows.toLocaleString()}</span>
            )}
            {metric.saves !== undefined && metric.saves > 0 && (
              <span>🔖 {metric.saves.toLocaleString()}</span>
            )}
          </div>
          {metric.notes && (
            <div className={styles.metricNotes}>{metric.notes}</div>
          )}
        </div>

        {/* 액션 버튼 (배치 모드가 아닐 때만) */}
        {!isBatchMode && (
          <div className={styles.metricItemActions}>
            <button
              type="button"
              className={styles.editBtn}
              onClick={() => startEdit(metric)}
              disabled={isLoading}
            >
              수정
            </button>
            <button
              type="button"
              className={`${styles.actionBtn} ${styles.deleteBtn}`}
              onClick={() => handleDeleteMetric(metric.id)}
              disabled={isLoading}
            >
              삭제
            </button>
          </div>
        )}
      </div>
    )
  }

  // --------------------------------------------------------------------------
  // Main Render
  // --------------------------------------------------------------------------
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={`${styles.modal} ${styles.metricManageModal}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className={styles.metricManageHeader}>
          <h3>메트릭 관리 - {post.title}</h3>
          <span className={styles.metricCount}>
            총 {post.metrics.length}개
          </span>
        </div>

        {/* 툴바 */}
        <div className={styles.metricToolbar}>
          <button
            type="button"
            className={`${styles.actionBtn} ${isAddMode ? styles.active : ''}`}
            onClick={isAddMode ? cancelAdd : startAdd}
            disabled={isLoading || isBatchMode}
          >
            {isAddMode ? '추가 취소' : '+ 메트릭 추가'}
          </button>

          <div className={styles.toolbarSpacer} />

          {!isAddMode && (
            <>
              <button
                type="button"
                className={`${styles.actionBtn} ${isBatchMode ? styles.active : ''}`}
                onClick={toggleBatchMode}
                disabled={isLoading || sortedMetrics.length === 0}
              >
                {isBatchMode ? '선택 취소' : '일괄 선택'}
              </button>

              {isBatchMode && (
                <>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={selectAllMetrics}
                    disabled={isLoading}
                  >
                    전체 선택
                  </button>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={clearSelection}
                    disabled={isLoading || selectedMetricIds.length === 0}
                  >
                    선택 해제
                  </button>
                  <button
                    type="button"
                    className={`${styles.actionBtn} ${styles.deleteBtn}`}
                    onClick={handleBatchDelete}
                    disabled={isLoading || selectedMetricIds.length === 0}
                  >
                    {selectedMetricIds.length}개 삭제
                  </button>
                </>
              )}
            </>
          )}
        </div>

        {/* 새 메트릭 추가 폼 */}
        {isAddMode && (
          <div className={styles.addMetricSection}>
            <h4>새 메트릭 추가</h4>
            {renderMetricForm(
              newMetric,
              (updates) => setNewMetric((prev) => ({ ...prev, ...updates })),
              saveNewMetric,
              cancelAdd,
              true
            )}
          </div>
        )}

        {/* 메트릭 목록 */}
        <div className={styles.metricList}>
          {sortedMetrics.length === 0 ? (
            <div className={styles.emptyMetrics}>
              <p>등록된 메트릭이 없습니다.</p>
              <p>메트릭을 추가하여 성과를 트래킹하세요!</p>
            </div>
          ) : (
            sortedMetrics.map(renderMetricItem)
          )}
        </div>

        {/* 푸터 */}
        <div className={styles.modalActions}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onClose}
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  )
}
